import { internalFetch } from './fetchHelper';
import { getAccount, getDecodedToken } from './authHelper';

/**
 * Tenant `otherInfo` values come from the backend as strings (e.g. `"true"`,
 * `"false"`). Use `isTenantFlagOn` to read boolean flags — don't compare
 * directly to `true`.
 */
export interface ITenantOtherInfo {
  showAssetInLeftNav?: string;
  showCreateWork?: string;
  showAssetInCreateWork?: string;
  theme?: string;
  logo?: string;
  title?: string;
  /** Custom header subtitle. A space-only value hides the default. */
  subtitle?: string;
  /** Tenant-wide instruction shown on the details screen of every work. */
  standardInstruction?: string;
  titleStyle?: string;
  [key: string]: unknown;
}

/**
 * Returns true when a tenant `otherInfo` flag is the string `"true"` (case
 * insensitive). Handles the fact that tenant flags are stored as strings.
 */
export const isTenantFlagOn = (
  otherInfo: ITenantOtherInfo | undefined,
  key: keyof ITenantOtherInfo
): boolean => {
  const v = otherInfo?.[key];
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.toLowerCase() === 'true';
  return false;
};

export interface ITenant {
  id: number;
  name?: string;
  description?: string;
  otherInfo: ITenantOtherInfo;
}

/**
 * Reads the tenant id claim from the backend JWT (`tenantId`). Used as the
 * session-identity lookup key for `GET /a/tenant`. Tenant *configuration*
 * must not be read from the JWT itself — only the tenant id is.
 *
 * NOTE: this reflects the tenant that was active when the token was issued.
 * If the user switches tenants via `/a/changeTenant` without re-issuing the
 * token, this value is stale until the next login / token refresh. The
 * authoritative "current tenant right now" is the `currentTenant` field in
 * the `/a/authorize` response.
 */
export const getTenantIdFromToken = (): number | null => {
  const payload = getDecodedToken();
  const raw = payload?.tenantId as number | string | undefined;
  if (raw == null || raw === '') return null;
  // The backend uses 0 when the authenticated user has no current tenant.
  // A missing selection must not become a request for a nonexistent tenant.
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
};

/**
 * Fetches the current user's tenant from the backend.
 *
 * @param apiBaseUrl Backend API base URL (no trailing slash), e.g. `VITE_BACKEND_API_URL`.
 * @param apiKey Azure Functions access code. Pass `''` for MFE apps that don't need it.
 * @param abortController Used to cancel in-flight fetches on unmount.
 */
export const fetchCurrentTenant = async (
  apiBaseUrl: string,
  apiKey: string,
  abortController: AbortController
): Promise<ITenant | null> => {
  const tenantId = getTenantIdFromToken();
  if (tenantId == null) return null;

  return (await internalFetch(
    `${apiBaseUrl}/a/tenant?tenantId=${tenantId}&code=${apiKey}`,
    'GET',
    abortController
  )) as ITenant;
};

/**
 * `/a/authorize` response shape — at least the fields the FE consumes.
 */
export interface IAuthorizeResponse {
  token?: string;
  permissions?: number[];
  tenants: ITenant[];
  currentTenant: number | null;
}

/**
 * POSTs `/a/authorize` with the MSAL upn to retrieve the tenants the current
 * user has access to (and which one is currently active). Returns `null` when
 * the user is unauthenticated.
 */
export const fetchAuthorize = async (
  apiBaseUrl: string,
  apiKey: string,
  abortController: AbortController
): Promise<IAuthorizeResponse | null> => {
  const username = getAccount()?.username;
  if (!username) return null;
  const code = apiKey ? `?code=${apiKey}` : '';
  return (await internalFetch(
    `${apiBaseUrl}/a/authorize${code}`,
    'POST',
    abortController,
    JSON.stringify({ username })
  )) as IAuthorizeResponse;
};
